import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { PlanarTopologyFaceRow } from "building-types/buildingGraphFilter/entitySource";
import {
  BUILDING_GRAPH_PLANE_TYPES,
  PLANAR_TOPOLOGY_FACE_NODE_TYPES,
  type PropertyPath,
} from "building-types/buildingGraphFilter/propertyCatalog";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { getBuildingGraphPlaneTypeFromNormalToZAxisAngle } from "../../generationTools/generateBuildingGraph";
import {
  faceNodeIdGetTypesForOperand,
  planarTopologyFaceRowGetTopology,
  topologyFaceIsUnbounded,
  topologyFaceGetNodeIds,
} from "../entityFilterSelectors";
import {
  type EntityFilterSource,
  type PropertyDescriptorForPath,
  type PropertyValue,
} from "../entityFilterSourceTypes";

const PLANAR_TOPOLOGY_FACE_DESCRIPTORS: {
  [P in PropertyPath<"PLANAR_TOPOLOGY_FACE">]: PropertyDescriptorForPath<"PLANAR_TOPOLOGY_FACE", P>;
} = {
  topologyId: { kind: "SCALAR", type: "id" },
  faceIndex: { kind: "SCALAR", type: "number" },
  planeHash: { kind: "SCALAR", type: "string" },
  normalToZAxisAngle: { kind: "SCALAR", type: "number" },
  planeType: { kind: "SCALAR", type: "enum", values: BUILDING_GRAPH_PLANE_TYPES },
  isUnbounded: { kind: "SCALAR", type: "boolean" },
  hasAnyFaceEdge: { kind: "SCALAR", type: "boolean" },
  isPerimeter: { kind: "SCALAR", type: "boolean" },
  faceNodeIds: { kind: "SET", type: "id" },
  faceNodeTypes: { kind: "SET", type: "enum", values: PLANAR_TOPOLOGY_FACE_NODE_TYPES },
};

function readPlanarTopologyFaceProperty(
  row: PlanarTopologyFaceRow,
  path: PropertyPath<"PLANAR_TOPOLOGY_FACE">,
  building: BuildingGraphFilterBuilding,
): PropertyValue {
  const topology = planarTopologyFaceRowGetTopology(row, building);
  const faceEdgeIds = topology?.planarGraphFaceToFaceEdgeIds[row.faceIndex] ?? [];
  const faceNodeIds = topologyFaceGetNodeIds(topology, row.faceIndex);
  switch (path) {
    case "topologyId":
      return { kind: "SCALAR", value: row.topologyId };
    case "faceIndex":
      return { kind: "SCALAR", value: row.faceIndex };
    case "planeHash":
      return { kind: "SCALAR", value: topology?.planeHash };
    case "normalToZAxisAngle":
      return { kind: "SCALAR", value: topology?.normalToZAxisAngle };
    case "planeType":
      return {
        kind: "SCALAR",
        value: topology ? getBuildingGraphPlaneTypeFromNormalToZAxisAngle(topology.normalToZAxisAngle) : undefined,
      };
    case "isUnbounded":
      return {
        kind: "SCALAR",
        value: topologyFaceIsUnbounded(topology, row.faceIndex),
      };
    case "hasAnyFaceEdge":
      return { kind: "SCALAR", value: faceEdgeIds.length > 0 };
    case "isPerimeter": {
      if (!topology) {
        return { kind: "SCALAR", value: false };
      }
      const isUnbounded = topologyFaceIsUnbounded(topology, row.faceIndex);
      const hasOpposingOverlapFaceEdge = faceEdgeIds.some((edgeIndex) => {
        const edge = building.buildingGraph.faceEdges[edgeIndex];
        return edge?.faceEdgeType === "OVERLAP" && edge.faceEdgeDot < 0;
      });
      return { kind: "SCALAR", value: !hasOpposingOverlapFaceEdge && !isUnbounded };
    }
    case "faceNodeIds":
      return { kind: "SET", values: new Set(faceNodeIds) };
    case "faceNodeTypes": {
      const types = new Set<string>();
      for (const faceNodeId of faceNodeIds) {
        for (const type of faceNodeIdGetTypesForOperand(faceNodeId, building)) {
          types.add(type);
        }
      }
      return { kind: "SET", values: types };
    }
    default:
      return assertUnreachable(path);
  }
}

export const planarTopologyFaceSource: EntityFilterSource<"PLANAR_TOPOLOGY_FACE"> = {
  sourceId: "PLANAR_TOPOLOGY_FACE",
  *iterateRows(building) {
    for (const topologyId in building.buildingGraph.faceTopologies) {
      const topology = building.buildingGraph.faceTopologies[topologyId];
      if (!topology) {
        continue;
      }
      const faceCount = topology.planeTopology.planarGraph.faceHalfEdges.length;
      for (let faceIndex = 0; faceIndex < faceCount; faceIndex++) {
        yield { topologyId, faceIndex };
      }
    }
  },
  identity(row) {
    return `${row.topologyId}:${row.faceIndex}`;
  },
  descriptor(path) {
    return PLANAR_TOPOLOGY_FACE_DESCRIPTORS[path];
  },
  readProperty(row, path, building) {
    return readPlanarTopologyFaceProperty(row, path, building);
  },
};
